/**
 * Готовые шаблоны структуры (pivot) для analytics-целей Подбора.
 *
 * Каждый goal получает свой пул шаблонов, заточенный под типичные задачи
 * соответствующей роли. Пользователь видит чипы над pivot-редактором,
 * кликает один — пресет применяется, поля можно дальше править руками.
 *
 * Все шаблоны используют поля dimension/fact из мок-датасета
 * `assets/mocks/groupPricing/analytics-dataset.json` (preview). Когда
 * подключим реальный OLAP — поля останутся теми же или ляжет маппинг.
 */

import type { SelectionGoal } from "../../../models/eplex/Selection";
import type { GpPivotPreset } from "../../../models/eplex/GroupPricing";

export interface PivotTemplate {
  /** Стабильный ключ для key= в v-for. */
  id: string;
  /** Короткий заголовок чипа (RU, отображается пользователю). */
  label: string;
  /** Сжатое описание под чипом — что эта структура показывает. */
  description: string;
  preset: GpPivotPreset;
}

const COMPETITORS_TEMPLATES: PivotTemplate[] = [
  {
    id: "competitors-by-platform",
    label: "Артикул × Площадка",
    description: "Цена и срок у каждой площадки для каждой позиции",
    preset: {
      rows: ["article", "brand"],
      cols: ["supplier"],
      vals: [
        { field: "cost", agg: "min" },
        { field: "time_int", agg: "min" },
      ],
      filters: [],
    },
  },
  {
    id: "competitors-min-max",
    label: "Спред цены",
    description: "Минимум, среднее и максимум по позиции — насколько разлёт",
    preset: {
      rows: ["article", "brand"],
      cols: [],
      vals: [
        { field: "cost", agg: "min" },
        { field: "cost", agg: "avg" },
        { field: "cost", agg: "max" },
      ],
      filters: [],
    },
  },
  {
    id: "competitors-vs-ours",
    label: "Свои против внешних",
    description: "Где мы дороже/дешевле рынка по группам",
    preset: {
      rows: ["brand"],
      cols: ["is_competitor"],
      vals: [
        { field: "cost", agg: "min" },
        { field: "cost", agg: "avg" },
      ],
      filters: [],
    },
  },
];

const MONITORING_TEMPLATES: PivotTemplate[] = [
  {
    id: "monitoring-trend-by-platform",
    label: "По площадкам",
    description: "Сводка последнего цикла — цена и наличие по каждой площадке",
    preset: {
      rows: ["supplier"],
      cols: [],
      vals: [
        { field: "cost", agg: "avg" },
        { field: "count", agg: "sum" },
      ],
      filters: [],
    },
  },
  {
    id: "monitoring-coverage",
    label: "Покрытие позиций",
    description: "Сколько площадок предлагает каждый артикул",
    preset: {
      rows: ["article", "brand"],
      cols: [],
      vals: [
        { field: "count", agg: "sum" },
        { field: "cost", agg: "min" },
      ],
      filters: [],
    },
  },
  {
    id: "monitoring-by-city",
    label: "По городам",
    description: "Срез цены по географии — где дешевле / куда быстрее доставка",
    preset: {
      rows: ["city"],
      cols: [],
      vals: [
        { field: "cost", agg: "min" },
        { field: "time_int", agg: "avg" },
      ],
      filters: [],
    },
  },
];

const PRICING_TEMPLATES: PivotTemplate[] = [
  {
    id: "pricing-brand-spread",
    label: "Цены по брендам",
    description: "Минимум, среднее и максимум розничной цены в каждом бренде",
    preset: {
      rows: ["brand"],
      cols: [],
      vals: [
        { field: "cost", agg: "min" },
        { field: "cost", agg: "avg" },
        { field: "cost", agg: "max" },
      ],
      filters: [],
    },
  },
  {
    id: "pricing-by-category",
    label: "По категориям",
    description: "Средняя цена и срок по товарным категориям",
    preset: {
      rows: ["cat_label", "brand"],
      cols: [],
      vals: [
        { field: "cost", agg: "avg" },
        { field: "time_int", agg: "avg" },
      ],
      filters: [],
    },
  },
  {
    id: "pricing-supplier-matrix",
    label: "Категория × Поставщик",
    description: "Тепловая матрица: средняя цена в категории по поставщикам",
    preset: {
      rows: ["cat_label"],
      cols: ["supplier"],
      vals: [{ field: "cost", agg: "avg" }],
      filters: [],
    },
  },
];

const TEMPLATES_BY_GOAL: Partial<Record<SelectionGoal, PivotTemplate[]>> = {
  competitors: COMPETITORS_TEMPLATES,
  monitoring: MONITORING_TEMPLATES,
  pricing: PRICING_TEMPLATES,
};

/**
 * Возвращает пул pivot-шаблонов для конкретной цели. Для не-analytics целей
 * (procurement / tender / forecast) — пустой массив (структура не показывается).
 */
export function pivotTemplatesForGoal(goal: SelectionGoal | null | undefined): PivotTemplate[] {
  if (!goal) return [];
  return TEMPLATES_BY_GOAL[goal] ?? [];
}

/**
 * Первый шаблон цели — используется как разумный default при выборе goal в
 * wizard'е, чтобы пользователь сразу видел осмысленный pivot (не пустой).
 */
export function defaultPivotPresetForGoal(goal: SelectionGoal): GpPivotPreset | null {
  const templates = pivotTemplatesForGoal(goal);
  return templates[0]?.preset ?? null;
}
